"use client"

import { useCallback, useEffect, useState } from "react"
import { API_ENDPOINTS, getApiErrorDetail, useApi } from "@/lib/api"
import { parseDocEnvelopes, parseProviderConfigData } from "@/lib/global-config"
import { invalidateHubCounts } from "@/lib/hub-counts-refresh"
import {
  CredentialView,
  GROUP_NAMES,
  InternalOrganizationLookup,
  PROVIDERS,
  ProfileTypeLookup,
  ProviderConfigurationData,
  WORKER_PROFILE_TYPE_CODES,
} from "@/types/payroll"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { ProviderEditor } from "./provider-editor"

export interface ProviderDocs {
  configDoc: { id: string | null; data: ProviderConfigurationData } | null
  formatDoc: { id: string | null; isDefault: boolean } | null
  credential: CredentialView | null
}

export function ProvidersPage() {
  const { adminApi, payrollApi } = useApi()
  const { toast } = useToast()
  const { t } = useTranslation()

  const [selected, setSelected] = useState<string>(PROVIDERS[0])
  const [docsByProvider, setDocsByProvider] = useState<Record<string, ProviderDocs>>({})
  const [profileTypes, setProfileTypes] = useState<ProfileTypeLookup[]>([])
  const [internalOrgs, setInternalOrgs] = useState<InternalOrganizationLookup[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      // Provider config + offered formats come from AdminService (GlobalConfiguration
      // CRUD already exists there); credentials come from the Function App, which
      // owns encryption. Documents are PascalCase on the wire — parsed explicitly
      // (a generic case transform would corrupt provider field names).
      const [configDocsRaw, formatDocsRaw, credentialsResponse, profileTypesResponse, internalOrgsResponse] = await Promise.all([
        adminApi.get<unknown>(API_ENDPOINTS.ADMIN.CONFIG_BY_GROUP(GROUP_NAMES.providerConfiguration)),
        adminApi.get<unknown>(API_ENDPOINTS.ADMIN.CONFIG_BY_GROUP(GROUP_NAMES.paymentFileFormatType)),
        payrollApi.get<{ data: CredentialView[] }>(API_ENDPOINTS.PAYROLL.CREDENTIALS),
        // Tenant code-table Values label the profile types; raw codes are the
        // fallback when the lookup is unavailable (e.g. PhoenixSql unset locally).
        payrollApi
          .get<{ data: ProfileTypeLookup[] }>(API_ENDPOINTS.PAYROLL.PROFILE_TYPES)
          .catch(() => ({ data: WORKER_PROFILE_TYPE_CODES.map((code) => ({ code, value: code })) })),
        // Internal organizations feed the mapping dropdown; an unavailable
        // lookup degrades to free-typed fields, the save still works.
        payrollApi
          .get<{ data: InternalOrganizationLookup[] }>(API_ENDPOINTS.PAYROLL.INTERNAL_ORGANIZATIONS)
          .catch(() => ({ data: [] as InternalOrganizationLookup[] })),
      ])

      const configDocs = parseDocEnvelopes(configDocsRaw).map((d) => ({
        id: d.id,
        data: parseProviderConfigData(d.data, ""),
      }))
      const formatDocs = parseDocEnvelopes(formatDocsRaw).map((d) => {
        const raw = (d.data ?? {}) as Record<string, unknown>
        return {
          id: d.id,
          value: String(raw.Value ?? raw.value ?? ""),
          isDefault: (raw.IsDefault ?? raw.isDefault) === true,
        }
      })
      const credentials = credentialsResponse.data ?? []

      const next: Record<string, ProviderDocs> = {}
      for (const provider of PROVIDERS) {
        next[provider] = {
          configDoc: configDocs.find((d) => d.data.provider.toLowerCase() === provider.toLowerCase()) ?? null,
          formatDoc: formatDocs.find((d) => d.value.toLowerCase() === provider.toLowerCase()) ?? null,
          credential: credentials.find((c) => c.provider.toLowerCase() === provider.toLowerCase()) ?? null,
        }
      }

      setDocsByProvider(next)
      setProfileTypes(profileTypesResponse.data ?? [])
      setInternalOrgs(internalOrgsResponse.data ?? [])
    } catch (err) {
      setError(getApiErrorDetail(err) ?? t("providers.error.load"))
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="space-y-3 py-8 text-center text-sm">
          <p className="text-destructive">{error}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void load()
              invalidateHubCounts()
            }}
          >
            {t("common.tryAgain")}
          </Button>
        </CardContent>
      </Card>
    )
  }

  const docs = docsByProvider[selected] ?? { configDoc: null, formatDoc: null, credential: null }

  return (
    <div className="grid gap-4 md:grid-cols-[220px_1fr]">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{t("tabs.providers")}</CardTitle>
          <CardDescription>{t("providers.oneSetPerProvider")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-1 p-2">
          {PROVIDERS.map((provider) => {
            const providerDocs = docsByProvider[provider]
            const isLive = providerDocs?.configDoc?.data?.isEnabled === true
            return (
              <button
                key={provider}
                type="button"
                onClick={() => setSelected(provider)}
                className={cn(
                  "flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-muted",
                  selected === provider && "bg-muted font-medium"
                )}
              >
                <span>{provider}</span>
                <span
                  className={cn(
                    "text-xs",
                    isLive ? "text-green-700 dark:text-green-400" : "text-muted-foreground"
                  )}
                >
                  {isLive ? "●" : "○"} {t(isLive ? "providers.sending" : "providers.notSetUp")}
                </span>
              </button>
            )
          })}
        </CardContent>
      </Card>

      <ProviderEditor
        key={selected}
        provider={selected}
        docs={docs}
        profileTypes={profileTypes}
        internalOrganizations={internalOrgs}
        onSaved={() => {
          toast({ title: t("providers.toast.settingsSaved") })
          void load()
        }}
      />
    </div>
  )
}
